import React, { useState, useEffect } from 'react';
import { Check, Heart, Calendar, Mail, Download, Printer, Users, MessageCircle, Shield } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import emailjs from '@emailjs/browser';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';

const ThankYou = () => {
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customerDetails, setCustomerDetails] = useState({
    username: '',
    email: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  
  // Generate order ID and date for receipt
  const orderId = `VM-${Math.floor(100000 + Math.random() * 900000)}`;
  const orderDate = new Date().toLocaleDateString();
  const orderTime = new Date().toLocaleTimeString();

  // EmailJS configuration
  const emailjsConfig = {
    serviceId: 'service_f9yq7m3',
    templateId: 'template_jsv23ci',
    userId: 'F0NoeGxGsZj3PIXSU'
  };

  // Check authentication status and fetch user details
  useEffect(() => {
    const auth = getAuth();
    const db = getFirestore();
    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in, fetch additional user data from Firestore
        try {
          // First check the users collection for username
          const userDocRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userDocRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            setCustomerDetails({
              username: userData.username || 'Valued Supporter',
              email: user.email,
              paymentMethod: 'PayPal',
              amount: 75.00
            });
          } else {
            // If no user data, check profiles collection for username fallback
            const profileDocRef = doc(db, "profiles", user.uid);
            const profileSnap = await getDoc(profileDocRef);
            
            if (profileSnap.exists()) {
              const profileData = profileSnap.data();
              setCustomerDetails({
                username: profileData.username || 'Valued Supporter',
                email: user.email,
                paymentMethod: 'PayPal',
                amount: 75.00
              });
            } else {
              // Final fallback
              setCustomerDetails({
                username: 'Valued Supporter',
                email: user.email,
                paymentMethod: 'PayPal',
                amount: 75.00
              });
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Use fallback if Firebase fetch fails
          setCustomerDetails({
            username: 'Valued Supporter',
            email: user.email,
            paymentMethod: 'PayPal',
            amount: 75.00
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // User is not signed in, redirect to sign-in page
        console.log("No user is signed in, redirecting to sign-in page");
        navigate('/login'); // Adjust path as needed
      }
    });
    
    return () => unsubscribe();
  }, [navigate]);

  // Function to send email receipt
  const sendEmailReceipt = async () => {
    if (emailSent || isLoading || !customerDetails.email) return;
    
    setLoading(true);
    
    try {
      const templateParams = {
        to_email: customerDetails.email,
        to_name: customerDetails.username,
        order_id: orderId,
        order_date: orderDate,
        order_time: orderTime,
        amount: 'Donation Amount (Private)',
        service_name: 'VineMatched Emotional Support Services',
        payment_method: customerDetails.paymentMethod,
        reply_to: 'support@vinematched.com',
        cc_email: 'support@vinematched.com' // CC to support team
      };
      
      await emailjs.send(
        emailjsConfig.serviceId,
        emailjsConfig.templateId,
        templateParams,
        emailjsConfig.userId
      );
      
      setEmailSent(true);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send email receipt. Please contact support.');
    } finally {
      setLoading(false);
    }
  };
  
  // Send email receipt when customer details are loaded
  useEffect(() => {
    if (!isLoading && customerDetails.email && !emailSent) {
      sendEmailReceipt();
    }
  }, [isLoading, customerDetails.email, emailSent]);
  
  // Function to create receipt HTML content for printing
  const generateReceiptHTML = () => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>VineMatched Donation Receipt - ${orderId}</title>
          <style>
            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              margin: 0;
              padding: 0;
              color: #57534e;
              background-color: #fafaf9;
            }
            .receipt-container {
              max-width: 800px;
              margin: 0 auto;
              padding: 40px;
              background-color: white;
              box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            }
            .header {
              background: linear-gradient(135deg, #78716c, #57534e);
              color: white;
              padding: 30px;
              text-align: center;
              margin-bottom: 30px;
              border-radius: 8px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              font-size: 2.5rem;
              font-weight: 300;
              letter-spacing: 2px;
            }
            .header h2 {
              margin: 0 0 15px 0;
              font-size: 1.5rem;
              font-weight: 400;
              opacity: 0.9;
            }
            .header p {
              margin: 0;
              font-size: 1rem;
              opacity: 0.8;
            }
            .details {
              margin-bottom: 40px;
              border: 1px solid #e7e5e4;
              border-radius: 8px;
              overflow: hidden;
            }
            .detail-row {
              display: flex;
              justify-content: space-between;
              padding: 16px 24px;
              border-bottom: 1px solid #f5f5f4;
            }
            .detail-row:last-child {
              border-bottom: none;
            }
            .detail-row:nth-child(even) {
              background-color: #fafaf9;
            }
            .detail-label {
              font-weight: 600;
              color: #44403c;
            }
            .detail-value {
              color: #57534e;
              font-weight: 500;
            }
            .thank-you-section {
              background-color: #f5f5f4;
              padding: 30px;
              border-radius: 8px;
              margin-bottom: 30px;
              text-align: center;
            }
            .thank-you-section h3 {
              color: #44403c;
              margin: 0 0 15px 0;
              font-size: 1.5rem;
              font-weight: 600;
            }
            .thank-you-section p {
              color: #57534e;
              margin: 0 0 10px 0;
              line-height: 1.6;
            }
            .impact-box {
              background: linear-gradient(135deg, #f7f7f6, #f5f5f4);
              border: 1px solid #e7e5e4;
              border-radius: 8px;
              padding: 30px;
              margin-top: 30px;
            }
            .impact-title {
              color: #44403c;
              font-weight: 600;
              margin-bottom: 20px;
              font-size: 1.25rem;
            }
            .impact-item {
              margin-bottom: 12px;
              display: flex;
              align-items: flex-start;
              line-height: 1.5;
            }
            .check-icon {
              color: #16a34a;
              margin-right: 12px;
              font-weight: bold;
              margin-top: 2px;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-size: 14px;
              color: #78716c;
              padding-top: 30px;
              border-top: 2px solid #e7e5e4;
            }
            @media print {
              .no-print {
                display: none;
              }
              body {
                background-color: white;
              }
              .receipt-container {
                box-shadow: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt-container">
            <div class="header">
              <h1>VINEMATCHED</h1>
              <h2>DONATION RECEIPT</h2>
              <p>Professional Emotional Support Services</p>
            </div>
            
            <div class="details">
              <div class="detail-row">
                <span class="detail-label">Receipt ID:</span>
                <span class="detail-value">${orderId}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Date:</span>
                <span class="detail-value">${orderDate}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Time:</span>
                <span class="detail-value">${orderTime}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Donor Username:</span>
                <span class="detail-value">${customerDetails.username}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Email:</span>
                <span class="detail-value">${customerDetails.email}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Service:</span>
                <span class="detail-value">VineMatched Emotional Support Platform</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Donation Amount:</span>
                <span class="detail-value"><strong>Private</strong></span>
              </div>
            </div>
            
            <div class="thank-you-section">
              <h3>Thank you for your generous donation!</h3>
              <p>Your support helps us provide free, anonymous emotional support to those in crisis.</p>
              <p>For support inquiries: support@vinematched.com</p>
            </div>
            
            <div class="impact-box">
              <div class="impact-title">Your Donation Impact:</div>
              
              <div class="impact-item">
                <span class="check-icon">✓</span>
                <span>Supports free anonymous chat sessions for those in emotional distress</span>
              </div>
              <div class="impact-item">
                <span class="check-icon">✓</span>
                <span>Helps maintain our crisis support platform and infrastructure</span>
              </div>
              <div class="impact-item">
                <span class="check-icon">✓</span>
                <span>Enables comprehensive training for compassionate volunteer listeners</span>
              </div>
              <div class="impact-item">
                <span class="check-icon">✓</span>
                <span>Funds platform improvements and advanced safety features</span>
              </div>
              <div class="impact-item">
                <span class="check-icon">✓</span>
                <span>Provides hope and meaningful connection to isolated individuals</span>
              </div>
            </div>
            
            <div class="footer">
              <strong>VineMatched</strong><br>
              Providing Professional Anonymous Emotional Support<br>
              Committed to Privacy, Safety, and Compassionate Care
            </div>
            
            <div class="no-print" style="text-align: center; margin-top: 30px;">
              <button onclick="window.print()" style="background-color: #78716c; color: white; padding: 12px 24px; border: none; border-radius: 6px; font-weight: 500; cursor: pointer;">Print this receipt</button>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  // Improved PDF download function with better error handling
  const downloadPdfReceipt = () => {
    try {
      // First show a loading indicator
      const loadingToast = document.createElement('div');
      loadingToast.className = 'fixed top-4 right-4 bg-stone-50 text-stone-700 px-4 py-2 rounded shadow-md';
      loadingToast.innerHTML = 'Generating PDF receipt...';
      document.body.appendChild(loadingToast);
      
      // Dynamically import jsPDF and html2canvas libraries
      Promise.all([
        import('jspdf'),
        import('html2canvas')
      ]).then(([jsPDFModule, html2canvasModule]) => {
        const jsPDF = jsPDFModule.default;
        const html2canvas = html2canvasModule.default;
        
        // Create a temporary container with receipt content
        const container = document.createElement('div');
        container.innerHTML = generateReceiptHTML();
        container.style.position = 'absolute';
        container.style.left = '-9999px';
        container.style.top = '-9999px';
        document.body.appendChild(container);
        
        // Allow some time for fonts and styles to load
        setTimeout(() => {
          // Convert HTML to canvas
          html2canvas(container.querySelector('.receipt-container'), {
            scale: 2,
            logging: false,
            useCORS: true
          }).then(canvas => {
            // Create PDF from canvas
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
              orientation: 'portrait',
              unit: 'mm',
              format: 'a4'
            });
            
            const imgWidth = 210; // A4 width in mm
            const pageHeight = 297; // A4 height in mm
            const imgHeight = canvas.height * imgWidth / canvas.width;
            
            pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
            pdf.save(`vinematched_donation_receipt_${orderId}.pdf`);
            
            // Clean up
            document.body.removeChild(container);
            document.body.removeChild(loadingToast);
          }).catch(error => {
            console.error('Error in html2canvas conversion:', error);
            document.body.removeChild(container);
            document.body.removeChild(loadingToast);
            handlePdfError();
          });
        }, 100);
      })
      .catch(error => {
        console.error('Error loading PDF libraries:', error);
        document.body.removeChild(loadingToast);
        handlePdfError();
      });
    } catch (error) {
      console.error('Unexpected error in PDF generation:', error);
      handlePdfError();
    }
  };

  // Handle PDF errors
  const handlePdfError = () => {
    // Create a toast notification
    const errorToast = document.createElement('div');
    errorToast.className = 'fixed top-4 right-4 bg-red-50 text-red-700 px-4 py-2 rounded shadow-md';
    errorToast.innerHTML = 'PDF generation failed. Please try again or use the print option.';
    document.body.appendChild(errorToast);
    
    // Remove notification after 3 seconds
    setTimeout(() => {
      document.body.removeChild(errorToast);
    }, 3000);
  };
  
  // Function to print receipt
  const printReceipt = () => {
    // Create a printable version of the receipt
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateReceiptHTML());
    printWindow.document.close();
  };

  // Display loading state while fetching user data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto px-4 py-12 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-stone-600 mx-auto mb-4"></div>
            <p className="text-stone-600">Loading your receipt...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-200 flex flex-col">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto bg-zinc-200 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-yellow-800 to-yellow-900 py-12 px-8 text-center">
            <Heart className="text-white mx-auto mb-6" size={56} />
            <h1 className="text-4xl font-light text-white mb-3 tracking-wide">Thank You</h1>
            <p className="text-stone-100 text-lg font-light">Your generous donation has been received</p>
          </div>
          
          <div className="p-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8 flex items-start">
              <Check size={28} className="text-emerald-600 mr-4 mt-1 flex-shrink-0" />
              <div>
                <p className="font-semibold text-emerald-800 text-lg mb-2">Donation Successful</p>
                <p className="text-emerald-700 leading-relaxed">Your generous contribution to VineMatched's emotional support services has been processed successfully. You are making a meaningful difference in people's lives during their most vulnerable moments.</p>
              </div>
            </div>

            {/* Impact Message */}
            <div className="bg-stone-300 border border-stone-200 rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-semibold text-stone-800 mb-4 flex items-center">
                <Users className="mr-3" size={28} />
                Your Impact on Our Community
              </h2>
              <p className="text-stone-700 mb-6 leading-relaxed">
                Your donation directly supports individuals experiencing emotional crises, providing them with free, anonymous access to trained listeners when they need compassionate support most.
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center text-stone-700 py-2">
                  <MessageCircle size={20} className="mr-3 text-stone-600" />
                  <span>Free crisis support conversations</span>
                </div>
                <div className="flex items-center text-stone-700 py-2">
                  <Shield size={20} className="mr-3 text-stone-600" />
                  <span>Secure, anonymous platform</span>
                </div>
                <div className="flex items-center text-stone-700 py-2">
                  <Heart size={20} className="mr-3 text-stone-600" />
                  <span>Continuous emotional support</span>
                </div>
                <div className="flex items-center text-stone-700 py-2">
                  <Users size={20} className="mr-3 text-stone-600" />
                  <span>Professional listener training</span>
                </div>
              </div>
            </div>
            
            {/* Receipt Section */}
            <div className="border border-stone-200 rounded-lg p-8 mb-8 bg-neutral-300">
              <h2 className="text-2xl font-semibold mb-6 flex items-center text-stone-800">
                <span>Donation Receipt</span>
                {emailSent && (
                  <span className="ml-4 text-sm bg-emerald-100 text-emerald-800 py-1 px-3 rounded-full font-medium">
                    Email Sent
                  </span>
                )}
              </h2>
              
              <div className="space-y-0 text-sm border border-stone-200 rounded-lg overflow-hidden">
                <div className="flex justify-between py-4 px-6 border-b border-stone-200 bg-white">
                  <span className="text-stone-600 font-semibold">Receipt ID:</span>
                  <span className="font-medium text-stone-800">{orderId}</span>
                </div>
                <div className="flex justify-between py-4 px-6 border-b border-stone-200">
                  <span className="text-stone-600 font-semibold">Date:</span>
                  <span className="text-stone-800">{orderDate}</span>
                </div>
                <div className="flex justify-between py-4 px-6 border-b border-stone-200 bg-white">
                  <span className="text-stone-600 font-semibold">Donor Username:</span>
                  <span className="text-stone-800">{customerDetails.username}</span>
                </div>
                <div className="flex justify-between py-4 px-6 border-b border-stone-200">
                  <span className="text-stone-600 font-semibold">Email:</span>
                  <span className="text-stone-800">{customerDetails.email}</span>
                </div>
                <div className="flex justify-between py-4 px-6 border-b border-stone-200 bg-white">
                  <span className="text-stone-600 font-semibold">Service:</span>
                  <span className="text-stone-800">Emotional Support Platform</span>
                </div>
                <div className="flex justify-between py-4 px-6 border-b border-stone-200">
                  <span className="text-stone-600 font-semibold">Donation Amount:</span>
                  <span className="font-medium text-stone-500">Private</span>
                </div>
              </div>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <button 
                  onClick={downloadPdfReceipt}
                  className="flex items-center bg-stone-100 hover:bg-stone-200 text-stone-700 py-3 px-6 rounded-lg transition-colors duration-200 font-medium"
                >
                  <Download size={18} className="mr-2" />
                  Download PDF Receipt
                </button>
                <button 
                  onClick={printReceipt}
                  className="flex items-center bg-neutral-100 hover:bg-neutral-200 text-neutral-700 py-3 px-6 rounded-lg transition-colors duration-200 font-medium"
                >
                  <Printer size={18} className="mr-2" />
                  Print Receipt
                </button>
              </div>
              
              {error && (
                <p className="mt-4 text-red-600 bg-red-50 p-3 rounded">{error}</p>
              )}
              
              <p className="mt-6 text-stone-500 leading-relaxed">
                A copy of this receipt has been sent to your email address. Your donation amount is kept private for your security and confidentiality.
              </p>
            </div>

            {/* What Your Donation Supports */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-8 mb-8">
              <h3 className="text-xl font-semibold text-neutral-800 mb-6">How Your Donation Creates Impact</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <Check size={20} className="text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">Maintains our free, anonymous chat platform for comprehensive crisis support</span>
                </div>
                <div className="flex items-start">
                  <Check size={20} className="text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">Funds extensive training programs for volunteer listeners and emotional support specialists</span>
                </div>
                <div className="flex items-start">
                  <Check size={20} className="text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">Supports platform improvements and advanced security features to protect user privacy</span>
                </div>
                <div className="flex items-start">
                  <Check size={20} className="text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">Enables continuous availability for those experiencing depression, anxiety, and emotional distress</span>
                </div>
                <div className="flex items-start">
                  <Check size={20} className="text-emerald-500 mr-4 mt-1 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">Provides hope and meaningful human connection to individuals who feel isolated</span>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="mb-8">
              <h3 className="text-2xl font-semibold mb-6 text-stone-800">Continue Supporting Our Mission</h3>
              
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-stone-100 p-3 rounded-full mr-6 flex-shrink-0">
                    <MessageCircle className="text-stone-600" size={24} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-stone-800 mb-2">Spread Awareness</h4>
                    <p className="text-stone-600 leading-relaxed">Help others discover our free emotional support services by sharing information about our platform with those who might benefit.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center border-t border-stone-200 pt-10">
              <h4 className="font-semibold mb-6 text-stone-800 text-lg">Need Support or Have Questions?</h4>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mb-8">
                <a 
                  href="mailto:support@vinematched.com" 
                  className="flex items-center text-stone-600 hover:text-stone-700 transition-colors font-medium"
                >
                  <Mail size={20} className="mr-2" />
                  <span>support@vinematched.com</span>
                </a>
                <span className="hidden sm:block text-stone-300">|</span>
                <a 
                  href="/chat/nhm1gYlqiLR3Wfo5rGssZx7dRhI3" 
                  className="flex items-center text-stone-600 hover:text-stone-700 transition-colors font-medium"
                >
                  <MessageCircle size={20} className="mr-2" />
                  <span>Get Emotional Support</span>
                </a>
              </div>
              
              <a 
                href="/" 
                className="inline-block bg-amber-800 hover:bg-stone-700 text-white py-4 px-10 rounded-lg font-medium transition duration-300 shadow-md hover:shadow-lg"
              >
                Return to Homepage
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ThankYou;